import { CategoryItem } from './categories.types';
import api from '../../utils/api';

export class CategoriesModel {
  private static STORAGE_KEY = 'mastercoifa_categories_db';

  private static defaultCategories: CategoryItem[] = [];

  public static getCategories(): CategoryItem[] {
    const raw = localStorage.getItem(this.STORAGE_KEY);
    if (!raw) {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.defaultCategories));
      return this.defaultCategories;
    }
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        // Remove categorias fictícias antigas de simulações passadas para garantir que use apenas as reais do banco
        const fakes = ["Coifas Residenciais", "Coifas Industriais", "Dutos e Conexões", "Motores e Exautores", "Partes Estruturais"];
        return parsed.filter(c => !fakes.includes(c.name));
      }
      return this.defaultCategories;
    } catch {
      return this.defaultCategories;
    }
  }

  public static saveCategories(categories: CategoryItem[]): void {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(categories));
  }

  public static async fetchCategories(): Promise<CategoryItem[]> {
    try {
      const response = await api.get('/categories');
      if (response.data && Array.isArray(response.data)) {
        const list = response.data.map((c: any) => ({
          id: Number(c.id),
          code: '',
          name: c.name,
          parentId: c.parentId !== null ? Number(c.parentId) : null
        }));
        this.rebuildCategoryCodes(list);
        this.saveCategories(list);
        return list;
      }
    } catch (err) {
      console.warn("Categories API Offline or Error, falling back to local storage:", err);
    }
    return this.getCategories();
  }

  public static async addCategoryToServer(name: string, parentId: number | null): Promise<{ success: boolean; error?: string }> {
    try {
      const localResult = this.addCategory(name, parentId);
      if (!localResult.success) {
        return localResult;
      }
      
      await api.post('/categories', {
        name: name.trim(),
        parentId
      });
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message || 'Erro ao salvar categoria no servidor.' };
    }
  }

  public static async updateCategoryOnServer(id: number, name: string, parentId: number | null): Promise<{ success: boolean; error?: string }> {
    try {
      const localResult = this.updateCategory(id, name, parentId);
      if (!localResult.success) {
        return localResult;
      }

      await api.post('/categories', {
        id,
        name: name.trim(),
        parentId
      });
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message || 'Erro ao atualizar categoria no servidor.' };
    }
  }

  public static async deleteCategoryFromServer(id: number): Promise<{ success: boolean; error?: string }> {
    try {
      const localResult = this.deleteCategory(id);
      if (!localResult.success) {
        return localResult;
      }

      await api.post('/categories/delete', { id });
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message || 'Erro ao remover categoria no servidor.' };
    }
  }

  public static addCategory(name: string, parentId: number | null): { success: boolean; error?: string } {
    const list = this.getCategories();

    if (!name || !name.trim()) {
      return { success: false, error: 'O nome da categoria não pode ser vazio.' };
    }

    if (parentId !== null) {
      // Rule validation: a subcategory cannot receive another subcategory
      const parent = list.find(c => c.id === parentId);
      if (!parent) {
        return { success: false, error: 'Categoria pai selecionada não existe.' };
      }
      if (parent.parentId !== null) {
        return { success: false, error: 'Uma subcategoria não pode receber outra subcategoria (limite de 2 níveis).' };
      }

      // Rule validation: up to two subcategories
      const sisters = list.filter(c => c.parentId === parentId);
      if (sisters.length >= 2) {
        return { success: false, error: `A categoria de nível superior "${parent.name}" já possui o limite máximo de 2 subcategorias.` };
      }
    }

    const maxId = list.reduce((acc, curr) => curr.id > acc ? curr.id : acc, 0);
    const newId = maxId + 1;

    // Create a sequential code format e.g. "01", "02"... for main categories
    let codeStr = '';
    if (parentId === null) {
      const mainCount = list.filter(c => c.parentId === null).length;
      codeStr = String(mainCount + 1).padStart(2, '0');
    } else {
      const parentIndex = list.filter(c => c.parentId === null).findIndex(c => c.id === parentId);
      const parentCode = parentIndex >= 0 ? String(parentIndex + 1).padStart(2, '0') : '00';
      const childCount = list.filter(c => c.parentId === parentId).length;
      codeStr = `${parentCode}.${childCount + 1}`;
    }

    const newCat: CategoryItem = {
      id: newId,
      code: codeStr,
      name: name.trim(),
      parentId
    };

    list.push(newCat);
    this.saveCategories(list);
    return { success: true };
  }

  public static updateCategory(id: number, name: string, parentId: number | null): { success: boolean; error?: string } {
    const list = this.getCategories();
    const index = list.findIndex(c => c.id === id);
    if (index === -1) {
      return { success: false, error: 'Categoria não encontrada.' };
    }

    if (!name || !name.trim()) {
      return { success: false, error: 'O nome da categoria não pode ser vazio.' };
    }

    // Check preventing circular reference or too deep nesting
    if (parentId !== null) {
      if (parentId === id) {
        return { success: false, error: 'Uma categoria não pode ser subcategoria dela mesma.' };
      }

      const parent = list.find(c => c.id === parentId);
      if (!parent) {
        return { success: false, error: 'Categoria pai selecionada não existe.' };
      }
      if (parent.parentId !== null) {
        return { success: false, error: 'A categoria pai selecionada já é uma subcategoria (limite máximo de 2 níveis).' };
      }

      // Check sisters rule excluding self
      const sisters = list.filter(c => c.parentId === parentId && c.id !== id);
      if (sisters.length >= 2) {
        return { success: false, error: `A categoria pai "${parent.name}" já possui o limite máximo de 2 subcategorias.` };
      }
    }

    // Check if we are demoting a category that itself has subcategories
    if (parentId !== null) {
      const currentChildren = list.filter(c => c.parentId === id);
      if (currentChildren.length > 0) {
        return { success: false, error: 'Não é possível definir esta categoria como subcategoria porque ela já possui subcategorias vinculadas.' };
      }
    }

    list[index] = {
      ...list[index],
      name: name.trim(),
      parentId
    };

    // Rebuild codes to look neat and consistent
    this.rebuildCategoryCodes(list);
    this.saveCategories(list);

    return { success: true };
  }

  public static deleteCategory(id: number): { success: boolean; error?: string } {
    const list = this.getCategories();
    const hasChildren = list.some(c => c.parentId === id);
    if (hasChildren) {
      return { success: false, error: 'Não é possível remover uma categoria que possui subcategorias. Remova as subcategorias primeiro.' };
    }

    const filtered = list.filter(c => c.id !== id);
    this.rebuildCategoryCodes(filtered);
    this.saveCategories(filtered);
    return { success: true };
  }

  private static rebuildCategoryCodes(list: CategoryItem[]): void {
    const mainCategories = list.filter(c => c.parentId === null);
    mainCategories.forEach((main, mIdx) => {
      main.code = String(mIdx + 1).padStart(2, '0');
      const subCats = list.filter(c => c.parentId === main.id);
      subCats.forEach((sub, sIdx) => {
        sub.code = `${main.code}.${sIdx + 1}`;
      });
    });
  }
}
